import React, { useState, useCallback } from 'react'
import { useMutation } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Switch from '@material-ui/core/Switch'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'
import Box from '@material-ui/core/Box'
import SearchIcon from '@material-ui/icons/Search'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import config from '../../config'
import AuthService from '../AuthService'

const UPDATE_PRODUCT = gql`
	mutation UpdateProduct($productId: Int!, $product: ProductInput!) {
		updateProduct(productId: $productId, product: $product) {
			productId
			model
			productType
			isSmart
			batteryComponentType
			cecEquipmentCategory
			inverterType
		}
	}
`

function getAuthToken() {
	try {
		return new AuthService().getToken() || ''
	} catch (e) {
		return ''
	}
}

function confidenceColor(c) {
	if (c === 'high') return '#2e7d32'
	if (c === 'medium') return '#e65100'
	return '#c62828'
}

export default function ProductEnrichPanel({ open, onClose, product }) {
	const [isSmart, setIsSmart] = useState(
		product?.isSmart != null ? product.isSmart : ''
	)
	const [batteryComponentType, setBatteryComponentType] = useState(
		product?.batteryComponentType || ''
	)
	const [inverterType, setInverterType] = useState(
		product?.inverterType || ''
	)
	const [aiResult, setAiResult] = useState(null)
	const [aiLoading, setAiLoading] = useState(false)
	const [aiError, setAiError] = useState(null)
	const [saveStatus, setSaveStatus] = useState(null)

	const [updateProduct] = useMutation(UPDATE_PRODUCT)

	const isInverterProduct = product?.productType
		&& String(product.productType).toUpperCase() === 'INVERTER'

	const handleSave = useCallback(async () => {
		setSaveStatus(null)
		const input = {}
		if (isSmart !== '') input.isSmart = isSmart
		if (batteryComponentType) input.batteryComponentType = batteryComponentType
		if (inverterType) input.inverterType = inverterType

		try {
			await updateProduct({
				variables: {
					productId: product.productId,
					product: input,
				},
			})
			setSaveStatus('saved')
		} catch (err) {
			setSaveStatus('error')
		}
	}, [isSmart, batteryComponentType, inverterType, product, updateProduct])

	const handleAiSearch = useCallback(async () => {
		setAiLoading(true)
		setAiError(null)
		setAiResult(null)
		try {
			const token = getAuthToken()
			if (!token) {
				setAiError('You are not signed in. Please log in again.')
				return
			}
			const resp = await fetch(`${config.API_URL}/api/admin/products/enrich`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
				body: JSON.stringify({ productId: product.productId }),
			})
			const data = await resp.json().catch(() => ({}))
			if (resp.status === 401) {
				setAiError(data.message || data.title || 'Unauthorized. Sign in again or ensure you are a super admin.')
				return
			}
			if (data.success) {
				setAiResult(data)
			} else {
				setAiError(data.error || 'AI enrichment failed.')
			}
		} catch (err) {
			setAiError(err.message || 'Network error.')
		} finally {
			setAiLoading(false)
		}
	}, [product])

	const handleAcceptAi = useCallback(() => {
		if (!aiResult) return
		if (aiResult.suggestedIsSmart != null) setIsSmart(aiResult.suggestedIsSmart)
		if (aiResult.suggestedBatteryComponentType)
			setBatteryComponentType(aiResult.suggestedBatteryComponentType)
		if (aiResult.suggestedInverterType)
			setInverterType(aiResult.suggestedInverterType)
	}, [aiResult])

	if (!product) return null

	const cecSource = product.productSource === 'CEC_SYNC' ? 'CEC' : 'Manual'
	const currentSmartLabel = product.isSmart === true
		? 'Yes' : product.isSmart === false
			? 'No' : 'Unknown'

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				Enrich Product: {product.brand} {product.model}
			</DialogTitle>
			<DialogContent dividers>
				{/* Section 1: CEC Default */}
				<Box mb={2}>
					<Typography variant="subtitle2" gutterBottom>
						1. Auto (CEC Default)
					</Typography>
					<Typography variant="body2" color="textSecondary">
						Source: {cecSource}
					</Typography>
					<Typography variant="body2">
						Smart: <strong>{currentSmartLabel}</strong>
					</Typography>
					{!isInverterProduct && product.cecEquipmentCategory && (
						<Typography variant="body2" color="textSecondary">
							CEC equipment category:{' '}
							<strong>{product.cecEquipmentCategory}</strong>
						</Typography>
					)}
					{product.batteryComponentType && (
						<Typography variant="body2">
							Internal component type:{' '}
							<strong>{product.batteryComponentType}</strong>
						</Typography>
					)}
					{product.inverterType && (
						<Typography variant="body2">
							Inverter type: <strong>{product.inverterType}</strong>
						</Typography>
					)}
				</Box>

				<Divider />

				{/* Section 2: Manual Override */}
				<Box mt={2} mb={2}>
					<Typography variant="subtitle2" gutterBottom>
						2. Manual Override
					</Typography>
					<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
						<FormControl size="small" variant="outlined" fullWidth>
							<InputLabel>Is Smart</InputLabel>
							<Select
								value={isSmart === '' ? '' : String(isSmart)}
								onChange={(e) => {
									const v = e.target.value
									if (v === '') setIsSmart('')
									else setIsSmart(v === 'true')
								}}
								label="Is Smart"
							>
								<MenuItem value="">Unknown</MenuItem>
								<MenuItem value="true">Yes</MenuItem>
								<MenuItem value="false">No</MenuItem>
							</Select>
						</FormControl>

						{!isInverterProduct && (
							<FormControl size="small" variant="outlined" fullWidth>
								<InputLabel>Internal component type</InputLabel>
								<Select
									value={batteryComponentType}
									onChange={(e) => setBatteryComponentType(e.target.value)}
									label="Internal component type"
								>
									<MenuItem value="">—</MenuItem>
									<MenuItem value="STANDALONE">Standalone</MenuItem>
									<MenuItem value="MODULAR">Modular</MenuItem>
									<MenuItem value="MASTER">Master</MenuItem>
								</Select>
							</FormControl>
						)}

						{isInverterProduct && (
							<TextField
								size="small"
								variant="outlined"
								label="Inverter Type"
								value={inverterType}
								onChange={(e) => setInverterType(e.target.value)}
								fullWidth
							/>
						)}

						<Box display="flex" alignItems="center" style={{ gap: 8 }}>
							<Button
								variant="contained"
								color="primary"
								size="small"
								onClick={handleSave}
							>
								Save
							</Button>
							{saveStatus === 'saved' && (
								<Chip
									size="small"
									icon={<CheckCircleIcon />}
									label="Saved"
									style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
								/>
							)}
							{saveStatus === 'error' && (
								<Chip
									size="small"
									icon={<WarningIcon />}
									label="Save failed"
									style={{ backgroundColor: '#fce4ec', color: '#c62828' }}
								/>
							)}
						</Box>
					</Box>
				</Box>

				<Divider />

				{/* Section 3: AI Search */}
				<Box mt={2}>
					<Typography variant="subtitle2" gutterBottom>
						3. AI Search (OpenAI Web Search)
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Search the internet for product specs and let AI suggest values.
					</Typography>

					<Button
						variant="outlined"
						color="secondary"
						size="small"
						startIcon={aiLoading ? <CircularProgress size={16} /> : <SearchIcon />}
						onClick={handleAiSearch}
						disabled={aiLoading}
					>
						{aiLoading ? 'Searching...' : 'Search with AI'}
					</Button>

					{aiError && (
						<Box mt={1}>
							<Typography variant="body2" color="error">
								{aiError}
							</Typography>
						</Box>
					)}

					{aiResult && (
						<Box mt={2} p={1.5} bgcolor="#f5f5f5" borderRadius={4}>
							<Typography variant="body2">
								<strong>Smart:</strong>{' '}
								{aiResult.suggestedIsSmart === true
									? 'Yes' : aiResult.suggestedIsSmart === false
										? 'No' : 'Unknown'}
							</Typography>
							{aiResult.suggestedBatteryComponentType && (
								<Typography variant="body2">
									<strong>Battery component type:</strong>{' '}
									{aiResult.suggestedBatteryComponentType}
								</Typography>
							)}
							{aiResult.suggestedInverterType && (
								<Typography variant="body2">
									<strong>Inverter type:</strong> {aiResult.suggestedInverterType}
								</Typography>
							)}
							<Typography variant="body2" style={{ marginTop: 4 }}>
								<strong>Reasoning:</strong> {aiResult.reasoning}
							</Typography>
							<Chip
								size="small"
								label={`Confidence: ${aiResult.confidence}`}
								style={{
									marginTop: 6,
									backgroundColor: confidenceColor(aiResult.confidence),
									color: '#fff',
									fontWeight: 500,
								}}
							/>
							<Box mt={1}>
								<Button
									variant="contained"
									size="small"
									onClick={handleAcceptAi}
									style={{ backgroundColor: '#1565c0', color: '#fff' }}
								>
									Accept suggestion
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="default" size="small">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	)
}
